/**
 * =====================================================================
 * apps/web/src/lib/stripe/env.ts
 * =====================================================================
 * Configuración runtime de Stripe según entorno (dev/prod, HTTP/HTTPS).
 *
 * Filosofía:
 *   - UN solo código fuente para desarrollo y producción.
 *   - Los métodos de pago se derivan del contexto del navegador
 *     (protocolo) y de flags variables (env vars). Sin hardcode.
 *   - Apple Pay y Google Pay REQUIEREN HTTPS. Stripe.js oculta esos
 *     métodos y lanza warnings en consola si se sirve por HTTP.
 *   - Bizum funciona sobre HTTP y HTTPS pero requiere REDIRECT (Stripe
 *     abre app/SMS del banco), por eso solo se activa cuando el flujo
 *     de pago dispone de página de retorno.
 *
 * Overrides posibles (VITE_*, opcionales):
 *   VITE_STRIPE_FORCE_WALLETS=true   -> fuerza Apple/Google Pay (útil si
 *                                       usas dev con HTTPS local).
 *   VITE_STRIPE_DISABLE_WALLETS=true -> los oculta siempre.
 *   VITE_STRIPE_DISABLE_BIZUM=true   -> desactiva Bizum manualmente.
 *
 * NOTA IMPORTANTE:
 *   Aunque el frontend indique "auto", Stripe solo mostrará realmente
 *   los métodos que estén activos en el Dashboard y compatibles con el
 *   PaymentIntent (moneda, importe, `setup_future_usage`, etc). Es decir,
 *   el frontend actúa como whitelist, el backend/Dashboard como fuente
 *   de la verdad.
 * =====================================================================
 */

const boolFromEnv = (raw: unknown): boolean => {
  if (typeof raw !== "string") return false;
  const v = raw.trim().toLowerCase();
  return v === "1" || v === "true" || v === "yes" || v === "on";
};

/** ¿Estamos en un contexto HTTPS real? (localhost http NO cuenta) */
export function isHttps(): boolean {
  if (typeof window === "undefined") return true; // SSR: asumimos secure
  return window.location.protocol === "https:";
}

/**
 * ¿Debemos habilitar los wallets Apple Pay / Google Pay?
 *
 * True cuando:
 *   - Estamos en HTTPS, o
 *   - El override VITE_STRIPE_FORCE_WALLETS está a true.
 * False cuando:
 *   - VITE_STRIPE_DISABLE_WALLETS está a true (fuerza off).
 */
export function walletsEnabled(): boolean {
  if (boolFromEnv(import.meta.env.VITE_STRIPE_DISABLE_WALLETS)) return false;
  if (boolFromEnv(import.meta.env.VITE_STRIPE_FORCE_WALLETS)) return true;
  return isHttps();
}

/**
 * ¿Debemos permitir Bizum como método visible?
 *
 * Bizum funciona sobre HTTP también, pero requiere REDIRECT. El caller
 * debe garantizar que su flujo de pago tenga `return_url` y una página
 * capaz de reconciliar el estado tras el redirect.
 *
 * Además, el PaymentIntent debe:
 *   - Estar en EUR.
 *   - Tener `automatic_payment_methods.allow_redirects: 'always'`.
 *   - NO usar `setup_future_usage` (Bizum no tokeniza).
 *
 * Si alguna de esas condiciones no se cumple, Stripe simplemente no
 * mostrará Bizum aunque este helper devuelva `true`.
 */
export function bizumEnabled(): boolean {
  if (boolFromEnv(import.meta.env.VITE_STRIPE_DISABLE_BIZUM)) return false;
  return true;
}

/**
 * Config lista para pasar a <PaymentElement options.wallets>.
 * `'auto'` deja decidir a Stripe según capacidades reales; `'never'`
 * fuerza a ocultar (usado en dev http).
 */
export function paymentElementWalletsOption(): {
  applePay: "auto" | "never";
  googlePay: "auto" | "never";
} {
  const on = walletsEnabled();
  return {
    applePay: on ? "auto" : "never",
    googlePay: on ? "auto" : "never",
  };
}

/**
 * ¿Está realmente configurada la publishable key de Stripe?
 *
 * No basta con que empiece por "pk_": detecta placeholders habituales
 * (ej. `pk_test_REEMPLAZAR_CON_TU_CLAVE_TEST`), que harían que stripe.js
 * cargara con una clave inexistente y el PaymentElement fallara con 401.
 */
export function isStripePublishableKeyConfigured(key: string | undefined): boolean {
  if (!key) return false;
  if (!key.startsWith("pk_test_") && !key.startsWith("pk_live_")) return false;
  return !/REEMPLAZAR|YOUR[_-]?KEY|PLACEHOLDER|XXXX/i.test(key);
}